import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertCircle, ListTodo } from "lucide-react";
import { EmptyState } from "@/components/shared/EmptyState";
import { useLinearIssues, useLinearProjects, type LinearIssue } from "@/hooks/useLinear";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const PRIORITY_LABELS: Record<number, string> = {
  0: "Sem prioridade",
  1: "Urgente",
  2: "Alta",
  3: "Média",
  4: "Baixa",
};

const PRIORITY_CLASSES: Record<number, string> = {
  0: "bg-muted text-muted-foreground",
  1: "bg-bad-soft text-bad",
  2: "bg-warn-soft text-warn",
  3: "bg-accent text-primary",
  4: "bg-muted text-muted-foreground",
};

export function IssuePriorityBadge({ priority, compact }: { priority: number; compact?: boolean }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-[7px] font-bold ${compact ? "h-[22px] px-[9px] text-[10.5px]" : "h-[23px] px-2.5 text-[11px]"} ${PRIORITY_CLASSES[priority] ?? PRIORITY_CLASSES[0]}`}
    >
      {PRIORITY_LABELS[priority] ?? "—"}
    </span>
  );
}

export function IssueStateDot({ state }: { state: LinearIssue["state"] }) {
  return (
    <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: state.color }} />
      {state.name}
    </span>
  );
}

function initials(name?: string | null) {
  if (!name) return "—";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

const CHIP =
  "h-[34px] w-auto min-w-0 gap-[7px] rounded-[9px] border-border bg-muted px-3 text-[12.5px] text-muted-foreground focus:ring-0";

interface DeliveryIssuesProps {
  /** When set, pre-filters to a single Linear project (used by DeliveryProjectDetail). Hides the project selector. */
  lockedProjectId?: string;
}

export function DeliveryIssues({ lockedProjectId }: DeliveryIssuesProps) {
  const [projectFilter, setProjectFilter] = useState<string>(lockedProjectId ?? "all");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");

  const { data: projectsData } = useLinearProjects();
  const projects = projectsData?.projects?.nodes ?? [];

  const activeProjectId = lockedProjectId ?? (projectFilter !== "all" ? projectFilter : null);
  const { data, isLoading, error } = useLinearIssues(activeProjectId);
  const allIssues = data?.issues?.nodes ?? [];

  const issues = allIssues.filter((i) => {
    if (stateFilter !== "all" && i.state.type !== stateFilter) return false;
    if (priorityFilter !== "all" && String(i.priority) !== priorityFilter) return false;
    return true;
  });

  const stateTypes = [...new Set(allIssues.map((i) => i.state.type))];

  return (
    <section
      className={
        lockedProjectId
          ? "overflow-hidden rounded-xl border border-border"
          : "overflow-hidden rounded-2xl border border-border bg-card shadow-card"
      }
    >
      <div className="flex flex-wrap items-center gap-[9px] border-b border-border px-6 py-4">
        {!lockedProjectId && (
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className={CHIP}>
              <SelectValue placeholder="Todos os projetos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os projetos</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className={CHIP}>
            <SelectValue placeholder="Todos os status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            {stateTypes.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className={CHIP}>
            <SelectValue placeholder="Todas as prioridades" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as prioridades</SelectItem>
            {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="ml-auto text-[12.5px] text-muted-foreground">
          {issues.length} issue{issues.length !== 1 ? "s" : ""}
        </span>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <div className="flex items-start gap-3 p-6">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-bad" />
          <div>
            <p className="text-sm font-medium">Não foi possível conectar ao Linear</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Configure <code className="rounded bg-muted px-1 font-mono">LINEAR_API_KEY</code> via{" "}
              <code className="rounded bg-muted px-1 font-mono">
                supabase secrets set LINEAR_API_KEY=lin_api_xxxxx
              </code>
            </p>
          </div>
        </div>
      )}

      {!isLoading && !error && issues.length === 0 && (
        <EmptyState
          icon={<ListTodo className="h-5 w-5" />}
          title="Nenhuma issue encontrada."
          className="py-12"
        />
      )}

      {!isLoading &&
        !error &&
        issues.map((issue) => (
          <a
            key={issue.id}
            href={issue.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 border-b border-border px-6 py-3.5 transition-colors last:border-b-0 hover:bg-muted/40"
          >
            <span className="w-[62px] shrink-0 truncate font-mono text-[11.5px] text-muted-foreground">
              {issue.identifier ?? issue.id.slice(0, 6).toUpperCase()}
            </span>
            <span
              className="h-[9px] w-[9px] shrink-0 rounded-full"
              style={{ backgroundColor: issue.state.color }}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13.5px] font-semibold">{issue.title}</div>
              <div className="mt-0.5 truncate text-[11.5px] text-muted-foreground">
                {issue.project?.name ?? "Sem projeto"} · {issue.state.name}
              </div>
            </div>
            <IssuePriorityBadge priority={issue.priority} />
            <span className="w-[70px] shrink-0 text-right text-xs text-muted-foreground">
              {issue.dueDate
                ? format(new Date(issue.dueDate + "T12:00:00"), "dd/MM", { locale: ptBR })
                : "—"}
            </span>
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-[10.5px] font-bold text-muted-foreground"
              title={issue.assignee?.name ?? "Sem responsável"}
            >
              {initials(issue.assignee?.name)}
            </span>
          </a>
        ))}
    </section>
  );
}
